import { useState, useCallback } from 'react';
import type { ValidationError } from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { InsightObjectAttributeType } from '@atlassian/jira-servicedesk-insight-object-types/src/common/types/object-attribute-type.tsx';
import { toObjectTypeAttributeId } from '@atlassian/jira-servicedesk-insight-shared-types/src/common/types/shared-types/index.tsx';
import type { AttributeValue } from '../../common/utils/transform-attributes/index.tsx';
import { useAttribute } from '../store/index.tsx';

export const useEditableAttribute = (attributeType: InsightObjectAttributeType) => {
	const [attribute, { updateAttribute }] = useAttribute(toObjectTypeAttributeId(attributeType.id));
	const [error, setError] = useState<ValidationError | Error>();
	const [loading, setLoading] = useState<boolean>(false);

	const update = useCallback(
		({
			updatedValues,
			onSuccess: onSuccessCb,
			onError: onErrorCb,
		}: {
			updatedValues: AttributeValue;
			onSuccess?: () => void;
			onError?: (arg1: Error | ValidationError) => void;
		}) => {
			const { id: objectTypeAttributeId } = attributeType;
			updateAttribute({
				objectTypeAttributeId,
				updatedValues,
				onLoad: () => {
					setLoading(true);
				},
				onSuccess: () => {
					onSuccessCb?.();
					setLoading(false);
				},
				onError: (err) => {
					onErrorCb?.(err);
					setError(err);
					setLoading(false);
				},
			});
		},
		[attributeType, updateAttribute],
	);

	const clearError = useCallback(() => {
		setError(undefined);
	}, [setError]);

	return [
		{ attribute, loading, error },
		{ update, clearError },
	] as const;
};
